import { FilterMatchMode } from "primereact/api";
import { Column, ColumnEditorOptions, ColumnEvent } from "primereact/column";
import { Answer } from "../types/answer";
import { InputTextEditor } from "../components/Shared/InputTextEditor";
import { QuestionLocationMap, QuestionMap } from "../types/checklist";
import { AnswerEditor, getAnswerDisplayValue } from "../components/Shared/AnswerEditor";
import { PhotoButton } from "../components/Answer/PhotoButton";

type TranslateFunction = (key: string) => string;

interface AnswerWithLocation extends Answer {
    sectionTitle?: string;
    subsectionTitle?: string;
}

export const defaultAnswerFilters = {
    global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    answerId: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    questionText: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    value: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    dataDictionaryId: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    questionType: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    isNotable: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    sectionTitle: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    subsectionTitle: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    }
};

export const defaultAnswerColumns = (
    onCellEditComplete: (event: ColumnEvent) => void,
    t: TranslateFunction,
    questionMap?: QuestionMap,
    questionLocationMap?: QuestionLocationMap
) => {
    // Create a dynamic editor that uses the question map to determine the appropriate input
    const answerEditor = (options: ColumnEditorOptions) => {
        const rowData = options.rowData as Answer;
        const question = questionMap?.[rowData.questionId];
        return <AnswerEditor options={options} question={question} />;
    };

    // Create a body template that displays values in human-readable format
    const answerBodyTemplate = (rowData: Answer) => {
        const question = questionMap?.[rowData.questionId];
        return getAnswerDisplayValue(rowData.value, question);
    };

    // Get section title for a row (uses enriched field if available, falls back to location map)
    const getSectionTitle = (rowData: AnswerWithLocation) => {
        if (rowData.sectionTitle) return rowData.sectionTitle;
        const location = questionLocationMap?.[rowData.questionId];
        return location?.sectionTitle || "-";
    };

    // Get subsection title for a row (uses enriched field if available, falls back to location map)
    const getSubsectionTitle = (rowData: AnswerWithLocation) => {
        if (rowData.subsectionTitle) return rowData.subsectionTitle;
        const location = questionLocationMap?.[rowData.questionId];
        return location?.subsectionTitle || "-";
    };

    return [
        <Column
            key="sectionTitle"
            field="sectionTitle"
            header={t("answers.table.section")}
            filter
            filterPlaceholder={t("answers.table.searchBySection")}
            sortable
            frozen
            className="medium-column"
            showFilterMenu={false}
            body={getSectionTitle}
            filterField="sectionTitle"
        />,
        <Column
            key="subsectionTitle"
            field="subsectionTitle"
            header={t("answers.table.subsection")}
            filter
            filterPlaceholder={t("answers.table.searchBySubsection")}
            sortable
            className="medium-column"
            showFilterMenu={false}
            body={getSubsectionTitle}
            filterField="subsectionTitle"
        />,
        <Column
            key="answerId"
            field="answerId"
            header={t("answers.table.id")}
            filter
            filterPlaceholder={t("answers.table.searchById")}
            sortable
            className="medium-column"
            showFilterMenu={false}
        />,
        <Column
            key="questionText"
            field="questionText"
            header={t("answers.table.question")}
            filter
            filterPlaceholder={t("answers.table.searchByQuestion")}
            sortable
            className="large-column"
            showFilterMenu={false}
        />,
        <Column
            key="value"
            field="value"
            header={t("answers.table.answer")}
            filter
            filterPlaceholder={t("answers.table.searchByAnswer")}
            sortable
            className="medium-column"
            body={answerBodyTemplate}
            editor={answerEditor}
            onCellEditComplete={onCellEditComplete}
        />,
        <Column
            key="photos"
            header={t("answers.table.photos")}
            className="small-column"
            body={(rowData: Answer) => <PhotoButton questionId={rowData.questionId} />}
        />,
        <Column
            key="dataDictionaryId"
            field="dataDictionaryId"
            header={t("answers.table.dataDictionaryId")}
            filter
            filterPlaceholder={t("answers.table.searchByDataDictionaryId")}
            sortable
            className="medium-column"
            editor={InputTextEditor}
            onCellEditComplete={onCellEditComplete}
        />,
        <Column
            key="questionType"
            field="questionType"
            header={t("answers.table.type")}
            filter
            filterPlaceholder={t("answers.table.searchByType")}
            sortable
            className="medium-column"
            showFilterMenu={false}
        />,
        <Column
            key="isNotable"
            field="isNotable"
            header={t("answers.table.notableItem")}
            filter
            filterPlaceholder={t("answers.table.search")}
            sortable
            className="small-column"
            showFilterMenu={false}
            body={(rowData: Answer) => rowData.isNotable ? t("answers.table.yes") : t("answers.table.no")}
        />,
        <Column
            key="createdAt"
            field="createdAt"
            header={t("answers.table.createdAt")}
            sortable
            className="small-column"
            body={(rowData: Answer) => rowData.createdAt ? new Date(rowData.createdAt).toLocaleString() : '-'}
        />,
        <Column
            key="updatedAt"
            field="updatedAt"
            header={t("answers.table.updatedAt")}
            sortable
            className="small-column"
            body={(rowData: Answer) => rowData.updatedAt ? new Date(rowData.updatedAt).toLocaleString() : '-'}
        />
    ];
};

export const defaultInspectionListFilters = {
    global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    inspectionNumber: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    vesselName: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    vesselId: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    location: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    surveyorName: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    checklistType: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    checklistVersion: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    status: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    }
};

export const defaultInspectionListColumns = (t: TranslateFunction) => {
    return [
        <Column
            key="inspectionNumber"
            field="inspectionNumber"
            header={t("inspections.table.inspectionNumber")}
            filter
            filterPlaceholder={t("inspections.table.searchByNumber")}
            sortable
            frozen
            className="small-column"
            showFilterMenu={false}
        />,
        <Column
            key="inspectionDate"
            field="inspectionDate"
            header={t("inspections.table.inspectionDate")}
            sortable
            className="small-column"
            body={(rowData) => rowData.inspectionDate ? new Date(rowData.inspectionDate).toLocaleDateString() : '-'}
        />,
        <Column
            key="location"
            field="location"
            header={t("inspections.table.location")}
            filter
            filterPlaceholder={t("inspections.table.searchByLocation")}
            sortable
            className="medium-column"
            showFilterMenu={false}
        />,
        <Column
            key="vesselName"
            field="vesselName"
            header={t("inspections.table.vesselName")}
            filter
            filterPlaceholder={t("inspections.table.searchByVessel")}
            sortable
            className="medium-column"
            showFilterMenu={false}
        />,
        <Column
            key="vesselId"
            field="vesselId"
            header={t("inspections.table.imo")}
            filter
            filterPlaceholder={t("inspections.table.searchByImo")}
            sortable
            className="small-column"
            showFilterMenu={false}
        />,
        <Column
            key="surveyorName"
            field="surveyorName"
            header={t("inspections.table.surveyorName")}
            filter
            filterPlaceholder={t("inspections.table.searchBySurveyor")}
            sortable
            className="medium-column"
            showFilterMenu={false}
        />,
        <Column
            key="checklistType"
            field="checklistType"
            header={t("inspections.table.checklistType")}
            filter
            filterPlaceholder={t("inspections.table.searchByType")}
            sortable
            className="medium-column"
            showFilterMenu={false}
        />,
        <Column
            key="checklistVersion"
            field="checklistVersion"
            header={t("inspections.table.checklistVersion")}
            filter
            filterPlaceholder={t("inspections.table.searchByVersion")}
            sortable
            className="small-column"
            showFilterMenu={false}
        />,
        <Column
            key="status"
            field="status"
            header={t("inspections.table.status")}
            filter
            filterPlaceholder={t("inspections.table.searchByStatus")}
            sortable
            className="small-column"
            showFilterMenu={false}
        />
    ];
};
